import { useState } from 'react';
import { ContactCenterLogo } from '@components/ContactCenterLogo';
import { ChunkLoadFailureReason } from '@lib/chunkLoad/classifyChunkLoadFailure';
import { Button, Flex, Text } from '@servicetitan/anvil2';

export interface ChunkLoadErrorViewProps {
  /** Why the chunk failed. `undefined` while the cause is still being checked. */
  reason?: ChunkLoadFailureReason;
  /** Performs the reload. Only ever called from a user click. */
  onReload: () => void;
  /** Checked at click time; when true the user must confirm before reloading. */
  isOnCall: () => boolean;
  /**
   * Render within the surrounding layout's content area (no logo, not full
   * height) instead of as a full-page error screen.
   */
  inline?: boolean;
}

interface ChunkLoadCopy {
  title: string;
  message: string;
  action: string;
}

const getCopy = (reason?: ChunkLoadFailureReason): ChunkLoadCopy => {
  switch (reason) {
    case ChunkLoadFailureReason.STALE_DEPLOY:
      return {
        title: 'A new version is available',
        message:
          'A new version of Contact Center Pro is available. Refresh to continue.',
        action: 'Refresh',
      };
    case ChunkLoadFailureReason.NETWORK:
    case ChunkLoadFailureReason.SERVER:
    case ChunkLoadFailureReason.TRANSIENT:
      return {
        title: 'Connection problem',
        message:
          "We couldn't load this page. Check your connection and try again.",
        action: 'Try again',
      };
    case ChunkLoadFailureReason.UNKNOWN:
      return {
        title: "This page couldn't be loaded",
        message: 'Refresh to try again.',
        action: 'Refresh',
      };
    default:
      return {
        title: "This page couldn't be loaded",
        message: 'Checking for a new version...',
        action: 'Refresh',
      };
  }
};

/**
 * Shown instead of a generic error when a lazy chunk fails to load. Wording
 * depends on `reason` so that only a genuine stale deploy tells the user a new
 * version exists. Never reloads on its own: an active call or unsaved draft
 * must not be lost, so the user always triggers the reload and is asked to
 * confirm first when on a call.
 *
 * Built from Anvil components and static design tokens only, so it also renders
 * inside a bare `AnvilProvider` where the app's other providers are not mounted
 * (the global error boundary).
 */
export const ChunkLoadErrorView = ({
  reason,
  onReload,
  isOnCall,
  inline,
}: ChunkLoadErrorViewProps) => {
  const [confirmingReload, setConfirmingReload] = useState(false);
  const { title, message, action } = getCopy(reason);

  const handleClick = () => {
    if (!confirmingReload && isOnCall()) {
      setConfirmingReload(true);
      return;
    }
    onReload();
  };

  return (
    <Flex
      direction='column'
      alignItems='center'
      justifyContent='center'
      gap={3}
      className={`ta-center ${inline ? 'p-y-5' : 'h-100vh'}`}
    >
      {!inline && <ContactCenterLogo showIcon />}
      <Text el='h2' variant='headline'>
        {title}
      </Text>
      <output data-testid='ChunkLoadError-Message'>
        <Text inline>{message}</Text>
      </output>
      {confirmingReload && (
        <Text
          data-testid='ChunkLoadError-CallWarning'
          role='alert'
          className='c-red-600'
        >
          You are on a call. Refreshing will end it.
        </Text>
      )}
      <Button
        data-testid='ChunkLoadError-ReloadButton'
        appearance='primary'
        onClick={handleClick}
      >
        {confirmingReload ? `${action} anyway` : action}
      </Button>
    </Flex>
  );
};
